import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useAuth, useSignIn } from '@clerk/react';
import LogRocket from 'logrocket';
import {
  Alert,
  AlertDescription,
  Button,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { AlertCircle, ArrowLeft, Loader2, Mail, Phone } from 'lucide-react';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import {
  SUPPORTED_COUNTRIES,
  clerkMessage,
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';

const phoneSchema = z
  .object({
    countryCode: z.enum(['+1', '+91', '+63']),
    phoneNumber: z.string().min(1, 'Phone number is required'),
  })
  .superRefine((val, ctx) => {
    const err = validateForCountry(stripDigits(val.phoneNumber), val.countryCode);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phoneNumber'], message: err });
  });
type PhoneFormValues = z.infer<typeof phoneSchema>;

const emailSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .toLowerCase()
    .email('Please enter a valid email address'),
});
type EmailFormValues = z.infer<typeof emailSchema>;

type FinalizeFn = () => Promise<{ error?: unknown }>;

// Clerk rejects new sign-in API calls with `session_exists` when the browser
// already has an active session — typically because the user signed in on
// another tab while this tab sat in the background and Clerk's cross-tab sync
// never reached it. Reload so SessionLoader can route them to their dashboard.
function isSessionExistsError(err: unknown): boolean {
  const errors = (err as { errors?: { code?: string }[] } | null | undefined)?.errors;
  return Array.isArray(errors) && errors[0]?.code === 'session_exists';
}

// Used by both PhoneFlow and EmailFlow's finalize calls — shared because the
// redirect logic is identical regardless of method.
function useFinalizeRedirect(): FinalizeFn {
  const { signIn } = useSignIn();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to') || '/';
  return () =>
    signIn.finalize({
      navigate: ({ session, decorateUrl }) => {
        if (session?.currentTask) return;

        const url = decorateUrl(redirectTo);
        if (url.startsWith('http')) window.location.href = url;
        else navigate(url, { replace: true });
      },
    });
}

// Countdown timer in seconds. Used to throttle resend buttons so we don't burn
// through Clerk's 1-per-30s send-rate limit. Extracted to a hook to keep
// useEffect out of component bodies per CLAUDE.md.
function useCooldown(initialSeconds: number) {
  const [remaining, setRemaining] = useState(initialSeconds);
  useEffect(() => {
    if (remaining <= 0) return;
    const id = setTimeout(() => setRemaining(r => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);
  return {
    remaining,
    isActive: remaining > 0,
    restart: () => setRemaining(initialSeconds),
  };
}

// ---- PhoneFlow ----
// Self-contained: owns step state, both mutations, the form, OTP digits.
// No props from parent. Step transitions are driven by mutation success
// (onSuccess: setStep) rather than by reading signIn signal state — this
// eliminates the signal-lag flash bug class.
function PhoneFlow() {
  const { signIn, errors: clerkErrors } = useSignIn();
  const { isSignedIn } = useAuth();
  const finalize = useFinalizeRedirect();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to') || '/';
  const [step, setStep] = useState<'input' | 'otp'>('input');
  const [code, setCode] = useState('');
  const cooldown = useCooldown(30);

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    mode: 'onChange',
    defaultValues: { countryCode: '+1', phoneNumber: '' },
  });

  // Guard the cross-tab race: if the user signed in elsewhere while this
  // tab sat idle, sendCode would fail with `session_exists`. Skip the API
  // call and route them to their destination directly.
  const redirectIfSignedIn = (): boolean => {
    if (!isSignedIn) return false;
    navigate(redirectTo, { replace: true });
    return true;
  };

  const sendPhoneCode = useMutation({
    mutationFn: async (values: PhoneFormValues) => {
      if (redirectIfSignedIn()) return;
      const e164 = `${values.countryCode}${stripDigits(values.phoneNumber)}`;
      const { error } = await signIn.phoneCode.sendCode({ phoneNumber: e164 });
      if (error) throw error;
    },
    onSuccess: () => {
      setStep('otp');
      cooldown.restart();
    },
    onError: err => {
      if (isSessionExistsError(err)) window.location.reload();
    },
  });

  const verifyCode = useMutation({
    mutationFn: async (c: string) => {
      if (redirectIfSignedIn()) return;
      const { error: verifyError } = await signIn.phoneCode.verifyCode({ code: c });
      if (verifyError) throw verifyError;
      const { error: finalizeError } = await finalize();
      if (finalizeError) throw finalizeError;
    },
    onError: err => {
      if (isSessionExistsError(err)) window.location.reload();
    },
  });

  const resendCode = useMutation({
    mutationFn: async () => {
      if (redirectIfSignedIn()) return;
      const { error } = await signIn.phoneCode.sendCode();
      if (error) throw error;
    },
    onSuccess: () => {
      setCode('');
      verifyCode.reset();
      cooldown.restart();
    },
    onError: err => {
      if (isSessionExistsError(err)) window.location.reload();
    },
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = stripDigits(e.target.value);
    phoneForm.setValue('phoneNumber', formatByCountry(digits, phoneForm.getValues('countryCode')), {
      shouldValidate: true,
    });
  };

  const handleCountryCodeChange = (c: SupportedCountryCode) => {
    phoneForm.setValue('countryCode', c, { shouldValidate: true });
    const digits = stripDigits(phoneForm.getValues('phoneNumber'));
    // Reformat existing digits for the new country (formatting + validation
    // rules differ). Skip entirely when the field is empty so we don't trigger
    // a spurious "Phone number is required" error before the user has typed.
    if (digits) {
      phoneForm.setValue('phoneNumber', formatByCountry(digits, c), { shouldValidate: true });
    }
  };

  const goBackToInput = () => {
    setStep('input');
    setCode('');
    signIn.reset();
    sendPhoneCode.reset();
    verifyCode.reset();
    resendCode.reset();
  };

  const hasInlineFieldError = Object.values(clerkErrors.fields).some(f => f != null);
  const mutationError =
    clerkMessage(verifyCode.error as { longMessage?: string; message?: string } | null) ??
    clerkMessage(sendPhoneCode.error as { longMessage?: string; message?: string } | null);
  const displayError = hasInlineFieldError
    ? null
    : (mutationError ??
      (clerkErrors.global && clerkErrors.global.length > 0
        ? clerkMessage(clerkErrors.global[0])
        : null));

  if (step === 'input') {
    return (
      <CardContent className="space-y-8 px-6 pb-8 pt-10">
        <div className="space-y-2 py-2 text-center">
          <h1 className="text-xl font-bold text-gray-950">Welcome back</h1>
          <p className="text-muted-foreground mx-auto max-w-xs text-sm">
            Enter your phone number to receive a code
          </p>
        </div>
        <Form {...phoneForm}>
          <form
            onSubmit={phoneForm.handleSubmit(v => sendPhoneCode.mutate(v))}
            className="space-y-4"
          >
            <FormField
              control={phoneForm.control}
              name="phoneNumber"
              render={({ field }) => {
                const selected =
                  SUPPORTED_COUNTRIES.find(c => c.code === phoneForm.watch('countryCode')) ??
                  SUPPORTED_COUNTRIES[0];
                return (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Phone className="h-4 w-4 text-gray-400" />
                      Phone number
                    </FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <Select
                          value={selected.code}
                          onValueChange={v => handleCountryCodeChange(v as SupportedCountryCode)}
                          disabled={SUPPORTED_COUNTRIES.length <= 1}
                        >
                          <SelectTrigger
                            className="focus:border-primary focus:ring-primary h-12! w-30 rounded-lg border-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Country code"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SUPPORTED_COUNTRIES.map(c => (
                              <SelectItem
                                key={c.code}
                                value={c.code}
                              >
                                <span className="mr-1">{c.flag}</span>
                                {c.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="tel"
                          placeholder={selected.placeholder}
                          className="focus:border-primary focus:ring-primary h-12 flex-1 rounded-lg border-gray-200 transition-all duration-200"
                          autoFocus
                          {...field}
                          onChange={handlePhoneNumberChange}
                        />
                      </div>
                    </FormControl>
                    <div className="min-h-5">
                      <FormMessage />
                      {clerkErrors.fields.identifier && (
                        <p className="text-destructive text-sm">
                          {clerkMessage(clerkErrors.fields.identifier)}
                        </p>
                      )}
                    </div>
                  </FormItem>
                );
              }}
            />
            {displayError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{displayError}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className="w-full py-6"
              disabled={!phoneForm.formState.isValid || sendPhoneCode.isPending}
            >
              {sendPhoneCode.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send phone code
            </Button>
          </form>
        </Form>
      </CardContent>
    );
  }

  // step === 'otp'
  return (
    <>
      {verifyCode.isPending && (
        <div className="z-100 fixed inset-0 flex items-center justify-center bg-white/70">
          <Spinner className="text-primary size-8" />
        </div>
      )}
      <CardContent className="space-y-8 px-6 pb-8 pt-10">
        <div className="space-y-2 py-2 text-center">
          <h1 className="text-xl font-bold text-gray-950">Enter the text code</h1>
          <p className="text-muted-foreground mx-auto max-w-xs text-sm">
            We just sent a 6-digit code to your phone
          </p>
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            if (code.length === 6) verifyCode.mutate(code);
          }}
          className="space-y-6"
        >
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Phone className="h-4 w-4 text-gray-400" />
              Phone code
            </label>
            <p className="text-xs text-gray-600">
              Sent to <span className="font-medium text-gray-800">{signIn.identifier}</span>
            </p>
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              autoFocus
              containerClassName="w-full gap-3"
            >
              <InputOTPGroup className="flex-1">
                {[0, 1, 2].map(i => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-12 flex-1 text-base"
                  />
                ))}
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup className="flex-1">
                {[3, 4, 5].map(i => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-12 flex-1 text-base"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
            <button
              type="button"
              className="text-primary hover:text-primary-hover text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => resendCode.mutate()}
              disabled={cooldown.isActive || resendCode.isPending}
            >
              {cooldown.isActive ? `Resend in ${cooldown.remaining}s` : 'Resend text code'}
            </button>
          </div>

          {displayError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{displayError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full py-6"
            disabled={verifyCode.isPending}
          >
            {verifyCode.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign in
          </Button>

          <button
            type="button"
            className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
            onClick={goBackToInput}
          >
            <ArrowLeft className="h-4 w-4" />
            Edit phone number
          </button>
        </form>
      </CardContent>
    </>
  );
}

// ---- EmailFlow ----
// Self-contained: owns step state, the email form, both mutations.
// The full sign-in chain (sendLink → waitForVerification → finalize) lives
// inside one mutationFn — mirrors Clerk's custom-flow doc verbatim. The
// mutationFn keeps running as a plain Promise even if the user switches tabs
// or this component otherwise unmounts.
function EmailFlow() {
  const { signIn, errors: clerkErrors } = useSignIn();
  const { isSignedIn } = useAuth();
  const finalize = useFinalizeRedirect();
  const [searchParams] = useSearchParams();
  const emailHint = searchParams.get('email');
  const [step, setStep] = useState<'input' | 'wait'>('input');
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const cooldown = useCooldown(30);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<{ longMessage?: string; message?: string } | null>(
    null,
  );

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange',
    defaultValues: { email: emailHint ?? '' },
  });

  const goBackToInput = () => {
    setStep('input');
    setEmailAddress(null);
    setSubmitError(null);
    setIsSubmitting(false);
    signIn.reset();
  };

  const hasInlineFieldError = Object.values(clerkErrors.fields).some(f => f != null);
  // Detect Clerk's "signed_out" 401 bug on signIn.emailLink.waitForVerification.
  // The error fires before the user clicks the link; surfacing it as an alert
  // is misleading. handleSubmit's waitForVerification handler reloads on this
  // code so the session is recovered if the user has clicked the link.
  const isClerkSignedOutBug =
    submitError != null &&
    Array.isArray((submitError as { errors?: unknown[] }).errors) &&
    (submitError as { errors: { code?: string }[] }).errors[0]?.code === 'signed_out';
  const mutationError = isClerkSignedOutBug ? null : clerkMessage(submitError);
  // Suppress alerts once Clerk has us signed in (cross-tab sync race).
  const displayError =
    isSignedIn || hasInlineFieldError
      ? null
      : (mutationError ??
        (clerkErrors.global && clerkErrors.global.length > 0
          ? clerkMessage(clerkErrors.global[0])
          : null));

  // Doc-style sequential flow per Clerk's custom email-link guide. No
  // useMutation — replaces the previous mutation/long-poll split to rule out
  // React Query as a contributor to the "you're signed out" flash. Verbose
  // logging captures the actual Clerk error shape at each branch so we can
  // identify the failure mode from real data rather than guess.
  async function handleSubmit(values: EmailFormValues) {
    setSubmitError(null);
    setIsSubmitting(true);

    const { error: sendErr } = await signIn.emailLink.sendLink({
      emailAddress: values.email,
      verificationUrl: `${window.location.origin}/sign-in/verify`,
    });
    if (sendErr) {
      console.error('[EmailFlow] sendLink error:', JSON.stringify(sendErr, null, 2));
      if (isSessionExistsError(sendErr)) {
        window.location.reload();
        return;
      }
      setSubmitError(sendErr as { longMessage?: string; message?: string });
      setIsSubmitting(false);
      return;
    }

    setEmailAddress(values.email);
    setStep('wait');
    cooldown.restart();
    setIsSubmitting(false);

    const startedAt = Date.now();
    const { error: waitErr } = await signIn.emailLink.waitForVerification();
    if (waitErr) {
      const errorContext = {
        raw: JSON.stringify(waitErr, null, 2),
        elapsedMs: Date.now() - startedAt,
        signInStatus: signIn.status,
        signInId: signIn.id ?? '',
        firstFactorStatus: signIn.firstFactorVerification?.status ?? '',
        isSignedIn: String(isSignedIn),
      };
      console.error('[EmailFlow] waitForVerification error:', errorContext);
      try {
        LogRocket.captureMessage('[EmailFlow] Clerk waitForVerification error', {
          tags: { feature: 'auth', flow: 'email-link-signin' },
          extra: errorContext,
        });
      } catch {
        // LogRocket may not be initialized; ignore.
      }
      // HACK: Clerk SDK bug — waitForVerification returns 401 "signed_out" on
      // consecutive polls. Reload to recover; if the user has clicked the link,
      // post-reload they'll be signed in via cookies. See support ticket.
      // `session_exists` covers the case where the user signed in on another
      // tab while this one was polling — reload lands them on their dashboard.
      const innerCode = (waitErr as { errors?: { code?: string }[] }).errors?.[0]?.code;
      if (innerCode === 'signed_out' || innerCode === 'session_exists') {
        window.location.reload();
        return;
      }
      // Clerk's polling token expires after ~3 minutes and the SDK doesn't
      // refresh it mid-poll — the next poll returns 401 `authentication_invalid`.
      // The link itself may still be valid; bounce them back to the input step
      // (email is still in the form) so they can resend without losing context.
      if (innerCode === 'authentication_invalid') {
        setSubmitError({
          message: 'Verification timed out. Please request a new login link.',
        });
        setStep('input');
        setIsSubmitting(false);
        return;
      }
      setSubmitError(waitErr as { longMessage?: string; message?: string });
      setIsSubmitting(false);
      return;
    }

    if (signIn.firstFactorVerification?.status === 'expired') {
      console.error('[EmailFlow] verification expired');
      setSubmitError({ message: 'This verification link has expired. Please request a new one.' });
      setIsSubmitting(false);
      return;
    }

    if (signIn.status === 'complete') {
      const { error: finalizeError } = await finalize();
      if (finalizeError) {
        console.error('[EmailFlow] finalize error:', JSON.stringify(finalizeError, null, 2));
        setSubmitError(finalizeError as { longMessage?: string; message?: string });
        setIsSubmitting(false);
        return;
      }
    } else {
      console.warn('[EmailFlow] sign-in not complete after verification', {
        status: signIn.status,
        isSignedIn,
      });
      setIsSubmitting(false);
    }
  }

  if (step === 'input') {
    return (
      <CardContent className="space-y-8 px-6 pb-8 pt-10">
        <div className="space-y-2 py-2 text-center">
          <h1 className="text-xl font-bold text-gray-950">Welcome back</h1>
          <p className="text-muted-foreground mx-auto max-w-xs text-sm">
            Enter your email to receive a magic sign-in link
          </p>
        </div>
        <Form {...emailForm}>
          <form
            onSubmit={emailForm.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Mail className="h-4 w-4 text-gray-400" />
                    Email address
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <div className="min-h-5">
                    <FormMessage />
                    {clerkErrors.fields.identifier && (
                      <p className="text-destructive text-sm">
                        {clerkMessage(clerkErrors.fields.identifier)}
                      </p>
                    )}
                  </div>
                </FormItem>
              )}
            />
            {displayError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{displayError}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className="w-full py-6"
              disabled={!emailForm.formState.isValid || isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send login link
            </Button>
          </form>
        </Form>
      </CardContent>
    );
  }

  // step === 'wait'
  return (
    <CardContent className="space-y-6 px-6 py-8">
      <div className="space-y-2 py-2 text-center">
        <h1 className="text-xl font-bold text-gray-950">Login link sent to your email</h1>
        <p className="text-muted-foreground mx-auto max-w-xs text-sm">
          Click the link we just emailed. This tab will sign you in automatically.
        </p>
      </div>
      <div className="space-y-6">
        <div className="bg-primary/5 flex flex-col items-center gap-3 rounded-xl p-6 text-center">
          <div className="bg-primary/10 flex size-12 items-center justify-center rounded-full">
            <Mail className="text-primary size-6" />
          </div>
          <p className="text-sm text-gray-700">We sent a login link to</p>
          <p className="text-base font-medium text-gray-950">{emailAddress}</p>
          <p className="text-xs text-gray-600">
            Open your inbox and click the link to finish signing in. The link expires in 10 minutes.
          </p>
        </div>

        {!displayError && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Spinner className="text-primary size-4" />
            Waiting for you to click the link…
          </div>
        )}

        {!!displayError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{displayError}</AlertDescription>
          </Alert>
        )}

        <button
          type="button"
          className="text-primary hover:text-primary-hover block w-full text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => emailAddress && handleSubmit({ email: emailAddress })}
          disabled={cooldown.isActive || isSubmitting}
        >
          {isSubmitting
            ? 'Sending…'
            : cooldown.isActive
              ? `Resend in ${cooldown.remaining}s`
              : 'Resend login link'}
        </button>

        <button
          type="button"
          className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
          onClick={goBackToInput}
        >
          <ArrowLeft className="h-4 w-4" />
          Edit email address
        </button>
      </div>
    </CardContent>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
        <Link
          to="/signup"
          className="text-sm text-gray-600 transition-colors duration-200 hover:text-gray-800"
        >
          Don't have an account? <span className="text-primary font-medium">Sign up</span>
        </Link>
      </header>

      <main className="pt-18 flex flex-1 flex-col items-center justify-center px-4 pb-12 md:pt-20">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

export function Login() {
  return (
    <PageShell>
      <div className="bg-background overflow-hidden rounded-2xl border">
        <Tabs
          defaultValue="email"
          className="gap-0"
        >
          <TabsList className="border-border bg-muted/40 grid h-12 w-full grid-cols-2 gap-0 rounded-none border-b p-0">
            <TabsTrigger
              value="email"
              className="border-border data-[state=active]:bg-background h-full rounded-none border-0 border-r text-sm font-medium data-[state=active]:shadow-none"
            >
              <Mail className="mr-1 size-4" />
              Email
            </TabsTrigger>
            <TabsTrigger
              value="phone"
              className="data-[state=active]:bg-background h-full rounded-none border-0 text-sm font-medium data-[state=active]:shadow-none"
            >
              <Phone className="mr-1 size-4" />
              Phone
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="email"
            className="m-0"
          >
            <EmailFlow />
          </TabsContent>
          <TabsContent
            value="phone"
            className="m-0"
          >
            <PhoneFlow />
          </TabsContent>
        </Tabs>
      </div>
    </PageShell>
  );
}
